import {
  NodeEditor
} from "@blings/node-lineup-creator";
import { useMst } from "../../stores/Root";
import { useRef, useState, useEffect, useMemo } from "react";
import _ from "lodash";
import { Button } from "antd";
import PublishConfirmDialog from "../../components/Modals/NodeManager/PublishConfirmDialog";
import "./FlowDiagramPage.scss";
import { BlingsButton } from "../../components/BlingsButton/BlingsButton";
import { AsyncOpState } from "../../types/enums/async-op-states";
import { IProjectModel } from "../../stores/project/projectModel";
import { getSnapshot } from "mobx-state-tree";
import { IFlowDiagram } from "@blings/blings-player";

const EMPTY_FLOW_DIAGRAM: IFlowDiagram = { nodes: [], edges: [] };



export function FlowDiagramPage() {
  const { selectedProject }: { selectedProject: IProjectModel } = useMst(
    (store) => {
      return {
        selectedProject: store.projectsStore.selectedProject,
      };
    }
  );
  const nodeEditorRef = useRef<any>(null);

  const [flowDiagram, setFlowDiagram] = useState<IFlowDiagram | null>(null);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [saveButtonOpState, setSaveButtonOpState] = useState<AsyncOpState>(
    AsyncOpState.Changed // Initialize to Changed to be enabled
  );

  // Use the MST model's view to get the transformed flow diagram
  const transformedFlowDiagram = useMemo(() => {
    if (!selectedProject.flowDiagramDraft) {
      return EMPTY_FLOW_DIAGRAM;
    }
    
    return selectedProject.flowDiagramDraft.asNodeEditorFlowDiagram;
  }, [selectedProject.flowDiagramDraft]);

  // To clear timeouts if component unmounts
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  const getCurrentFlowDiagram = () => {
    if (nodeEditorRef.current) {
      const currentDiagram = nodeEditorRef.current.getFlowDiagram();
      return currentDiagram;
    }
  };

  async function saveFlow() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current); // Clear previous timeout if any
    }

    const currentFlowDiagram = getCurrentFlowDiagram();
    if (!currentFlowDiagram) {
      console.error("Flow diagram is null");
      setSaveButtonOpState(AsyncOpState.Error);
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return;
    }
    const storeFlowDiagramSnapshot = selectedProject.flowDiagram
      ? getSnapshot(selectedProject.flowDiagram)
      : null;
    const deepCompareResult = _.isEqual(
      currentFlowDiagram,
      storeFlowDiagramSnapshot
    );
    if (deepCompareResult) {
      setSaveButtonOpState(AsyncOpState.Success); // Indicate "Saved" even if no change
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return;
    }

    setSaveButtonOpState(AsyncOpState.Saving);
    try {
      setFlowDiagram(currentFlowDiagram);
      selectedProject.setFlowDiagramDraft(currentFlowDiagram);
      await selectedProject.saveFlowDiagramDraft();
      setSaveButtonOpState(AsyncOpState.Success);
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return currentFlowDiagram;
    } catch (error) {
      console.error("Failed to save flow diagram draft:", error);
      setSaveButtonOpState(AsyncOpState.Error);
      timeoutRef.current = setTimeout(
        () => setSaveButtonOpState(AsyncOpState.Changed),
        2000
      );
      return; // Explicitly return undefined on error
    }
  }
  async function publishFlow() {
    const currentFlowDiagram = await saveFlow();
    if (currentFlowDiagram) {
      // Assuming flowDiagram state is correctly set by saveFlow or elsewhere if needed
      // For publish, we might want to use the successfully saved currentFlowDiagram
      selectedProject.setFlowDiagram(currentFlowDiagram); // Use the returned diagram
      await selectedProject.saveFlowDiagram();
      // Potentially update opState for a publish button if there was one
    }
  }

  async function onChange(flowDiagram: IFlowDiagram) {
    setFlowDiagram(flowDiagram);
  }

  return (
    <div style={{ height: "80vh", width: "100vw" }} className="FlowDiagramPage">
      <div className="node-manager-header">
        <BlingsButton 
          className={"save-button"}
          onClick={saveFlow}
          opState={saveButtonOpState}
        />
        <Button
          className="publish-button"
          onClick={() => {
            setIsPublishDialogOpen(true);
          }}
        >
          Publish
        </Button>
      </div>
      <NodeEditor
      //@ts-ignore
        ref={nodeEditorRef as any}
        videoParts={selectedProject.publishedVideoParts.map(
          (part: any) => part.name
        )}
        dynamicData={selectedProject.stateJsonSchemaStr || "{}"}
        flowDiagram={transformedFlowDiagram}
        //onChange={onChange} // Commented out for now to avoid possible performance issues since the onChange of reactFlow is for EVERY change
      />

      <PublishConfirmDialog
        isOpen={isPublishDialogOpen}
        onClose={() => setIsPublishDialogOpen(false)}
        onConfirm={async () => {
          publishFlow && (await publishFlow());
        }}
      />
    </div>
  );
}
